import * as graphics from "@client/app/graphics";
import {calculateRoundedDayMetrics} from "@client/calculations";
import {BarSurface} from "@client/components/Glass";
import {TouchHandler} from "@client/components/TouchHandler";
import {useMedia} from "@client/hooks";
import NumberFlow from "@number-flow/react";
import {classNames, server} from "@reactivated";
import * as React from "react";

interface Props {
    day: server.journal.nutrition.Day.output;
    target: server.journal.nutrition.Target.output | null;
    useKilojoules: boolean;
    onClick?: () => void;
}

export const DaySummary = ({day, useKilojoules, ...props}: Props) => {
    if ([].length == 0) {
        return null;
    }

    const calculations = calculateRoundedDayMetrics(day, props.target, useKilojoules);

    const {total, remaining, target, percentages} = calculations;
    const macros = [
        {
            macro: "calories",
            unit: null,
            bg: "bg-rose-100/70",
            fill: "bg-rose-500/70",
            label: "Calories",
        },
        {
            macro: "protein",
            unit: "g",
            bg: "bg-blue-100/70",
            fill: "bg-blue-500/70",
            label: "Protein",
        },
        {
            macro: "fat",
            unit: "g",
            bg: "bg-blue-100/70",
            fill: "bg-blue-500/70",
            label: "Fat",
        },
        {
            macro: "carbs",
            unit: "g",
            bg: "bg-blue-100/70",
            fill: "bg-blue-500/70",
            label: "Carbs",
        },
        {
            macro: "net_carbs",
            unit: "g",
            bg: "bg-blue-100/70",
            fill: "bg-blue-500/70",
            label: "Net Carbs",
        },
    ] as const;

    return (
        <TouchHandler
            onClick={props.onClick}
            className="block items-center rounded-xl bg-white py-1 text-left"
        >
            <div className="flex items-center px-1 pr-2">
                <div className="flex flex-1 gap-1.5 *:flex-1">
                    {macros.map(({macro, unit, bg, fill, label}) => {
                        if (
                            target[macro] == null &&
                            macro !== "calories" &&
                            macro !== "protein"
                        ) {
                            return null;
                        }

                        if (macro == "carbs" && target.net_carbs != null) {
                            return null;
                        }

                        return (
                            <div key={macro} className="@container rounded-lg py-1">
                                <div className="@[9rem]:px-1.5">
                                    <div className="mb-1 flex flex-col items-center justify-between whitespace-nowrap @[9rem]:flex-row @[9rem]:px-1">
                                        <span className="mb-0.5 text-sm font-medium text-gray-800 @[9rem]:mb-0">
                                            {label}
                                        </span>
                                        <span className="text-xs text-gray-600">
                                            {total[macro]}{" "}
                                            {target[macro] != null
                                                ? `/ ${target[macro]}`
                                                : ""}{" "}
                                            {unit}
                                        </span>
                                    </div>
                                    <div className={`h-2 rounded-full ${bg} `}>
                                        <div
                                            className={`h-2 rounded-full ${fill} `}
                                            style={{
                                                width:
                                                    percentages[macro] != null
                                                        ? `${percentages[macro]}%`
                                                        : 0,
                                            }}
                                        ></div>
                                    </div>
                                    <div className="mt-1 text-center text-xs text-gray-700">
                                        {remaining[macro] != null
                                            ? `${remaining[macro]} ${unit ?? ""} left`
                                            : "No target"}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div
                    className={classNames(
                        "flex flex-col items-center justify-center h-5 opacity-80 -mr-1.5",
                    )}
                >
                    <graphics.ElGrecoCustomChevronRight />
                </div>
            </div>
        </TouchHandler>
    );
};

const CircularProgress = ({
    percentage,
    size = 80,
    strokeWidth = 6,
    color,
}: {
    percentage: string | null;
    size?: number;
    strokeWidth?: number;
    color: string;
}) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDashoffset =
        percentage != null
            ? circumference - (parseFloat(percentage) / 100) * circumference
            : null;
    return (
        <div className="relative" style={{width: size, height: size}}>
            <svg
                className="-rotate-90 transform"
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
            >
                {/* The disc behind the numbers: nothing on the glass scrolls
                    through the hole. */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius - strokeWidth / 2}
                    fill="#fff"
                    opacity={0.5}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                    fill="transparent"
                    stroke="rgb(68 64 60 / 0.16)"
                />
                {strokeDashoffset != null && (
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke="currentColor"
                        strokeWidth={strokeWidth}
                        fill="transparent"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="butt"
                        className={color}
                        style={{
                            transition: "stroke-dashoffset 0.5s ease-in-out",
                        }}
                    />
                )}
            </svg>
        </div>
    );
};

export const DaySummaryV2 = ({day, useKilojoules, ...props}: Props) => {
    const isTiny = useMedia("(max-width: 375px)");
    const calculations = calculateRoundedDayMetrics(day, props.target, useKilojoules);

    const {total, remaining, target, percentages} = calculations;
    const macros = [
        {
            macro: "calories",
            unit: null,
            bg: "text-rose-100/70",
            fill: "text-rose-500/70",
            label: "Calories",
        },
        {
            macro: "protein",
            unit: "g",
            bg: "text-blue-100/70",
            fill: "text-blue-500/70",
            label: "Protein",
        },
        {
            macro: "fat",
            unit: "g",
            bg: "text-blue-100/70",
            fill: "text-blue-500/70",
            label: "Fat",
        },
        {
            macro: "carbs",
            unit: "g",
            bg: "text-blue-100/70",
            fill: "text-blue-500/70",
            label: "Carbs",
        },
        {
            macro: "net_carbs",
            unit: "g",
            bg: "text-blue-100/70",
            fill: "text-blue-500/70",
            label: "Net Carbs",
        },
    ] as const;

    const visibleMacros = macros.filter(({macro}) => {
        if (macro == "carbs" && target.net_carbs != null) {
            return false;
        }

        if (macro == "net_carbs" && target.net_carbs == null) {
            return false;
        }

        return true;
    });

    return (
        <TouchHandler onClick={props.onClick}>
            <div className="flex gap-2.5">
                {visibleMacros.map(({macro, unit, fill, label}) => {
                    const percentage = percentages[macro] ?? null;

                    return (
                        <BarSurface key={macro} className="flex-1 rounded-xl">
                            <div className="flex flex-col items-center py-2">
                                <div className="mb-1.5 text-center text-[10.5px] font-normal tracking-[0.04em] text-[oklch(0.38_0.03_280)] uppercase">
                                    {label}
                                </div>
                                <div className="relative">
                                    <CircularProgress
                                        percentage={percentage}
                                        size={isTiny ? 68 : 70}
                                        strokeWidth={isTiny ? 7 : 8}
                                        color={fill}
                                    />
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <NumberFlow
                                            value={Number(total[macro] ?? 0)}
                                            className="missing-tailwind-macros-unit text-base font-light text-gray-950"
                                            suffix={
                                                unit != null ? ` ${unit}` : undefined
                                            }
                                        />
                                        {target[macro] != null && (
                                            <>
                                                <span className="hidden h-px w-5 bg-gray-200"></span>
                                                <span className="relative -mt-0.5 text-[11px] text-gray-500">
                                                    {target[macro]}
                                                    {unit}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="hidden text-center">
                                    <div className="text-xs text-gray-600">
                                        {remaining[macro] != null
                                            ? `${remaining[macro]} ${unit ?? ""} left`
                                            : "No target"}
                                    </div>
                                </div>
                            </div>
                        </BarSurface>
                    );
                })}
            </div>
        </TouchHandler>
    );
};
